import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const TOKEN_KEY = "listener_token";

export const api = axios.create({ baseURL: API, timeout: 30000 });

api.interceptors.request.use((config) => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (token) {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

api.interceptors.response.use(
    (r) => r,
    (error) => {
        const status = error?.response?.status;
        if (status === 401) {
            localStorage.removeItem(TOKEN_KEY);
            if (typeof window !== "undefined" && window.location.pathname !== "/login") {
                window.location.assign("/login");
            }
        }
        return Promise.reject(error);
    },
);

export const loginWithGoogle = (credential) =>
    api.post("/auth/google", { credential }).then((r) => r.data);
export const fetchMe = () => api.get("/auth/me").then((r) => r.data);

// --- Google Drive backup ---
export const driveStatus = () => api.get("/backup/drive/status").then((r) => r.data);
export const driveConnect = (code, redirect_uri) =>
    api.post("/backup/drive/connect", { code, redirect_uri }).then((r) => r.data);
export const driveDisconnect = () =>
    api.post("/backup/drive/disconnect").then((r) => r.data);
export const driveRunNow = () =>
    api.post("/backup/drive/run-now", null, { timeout: 90000 }).then((r) => r.data);

// --- Google Calendar (read-only) ---
export const calendarStatus = () => api.get("/calendar/status").then((r) => r.data);
export const calendarConnect = (code) => api.post("/calendar/connect", { code }).then((r) => r.data);
export const calendarDisconnect = () => api.post("/calendar/disconnect").then((r) => r.data);
export const listCalendarEvents = (days = 7) =>
    api.get("/calendar/events", { params: { days } }).then((r) => r.data);

// Session chat — post-session Q&A grounded in the actual session data.
// `model` ("opus" | "default") picks the answering tier; omitted = default.
export const sessionChat = (sid, question, model) =>
    api.post(`/sessions/${sid}/chat`, { question, ...(model ? { model } : {}) }, { timeout: 90000 }).then((r) => r.data);
export const clearSessionChat = (sid) =>
    api.delete(`/sessions/${sid}/chat`).then((r) => r.data);

// Download a session as PDF. The browser can't attach our JWT to a plain
// <a href> click, so we fetch as a blob and trigger the download in JS.
export async function downloadSessionPdf(sessionId, fallbackTitle = "session") {
    const resp = await api.get(`/sessions/${sessionId}/pdf`, { responseType: "blob" });
    // Pull filename out of Content-Disposition if present
    const cd = resp.headers["content-disposition"] || "";
    const match = /filename="?([^"]+)"?/i.exec(cd);
    const filename = match
        ? match[1]
        : `listener_${fallbackTitle.replace(/[^a-z0-9_-]+/gi, "_")}.pdf`;
    const url = URL.createObjectURL(resp.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
}

// Interviews only — meetings (kind="meeting") are excluded so the research
// Sessions surfaces never mix in note-taker meetings. folderId still works.
export const listSessions = (folderId) =>
    api.get("/sessions", { params: { kind: "interview", ...(folderId !== undefined ? { folder_id: folderId } : {}) } }).then((r) => r.data);

// Everything in a folder — BOTH interviews and meetings (no kind filter),
// since a folder can hold either.
export const listFolderItems = (folderId) =>
    api.get("/sessions", { params: { folder_id: folderId } }).then((r) => r.data);
export const createSession = (data) => api.post("/sessions", data).then((r) => r.data);
export const getSession = (id) => api.get(`/sessions/${id}`).then((r) => r.data);
export const updateSession = (id, data) => api.patch(`/sessions/${id}`, data).then((r) => r.data);
export const deleteSession = (id) => api.delete(`/sessions/${id}`).then((r) => r.data);

export const listFolders = () => api.get("/folders").then((r) => r.data);
export const createFolder = (data) => api.post("/folders", data).then((r) => r.data);
export const getFolder = (id) => api.get(`/folders/${id}`).then((r) => r.data);
export const updateFolder = (id, data) => api.patch(`/folders/${id}`, data).then((r) => r.data);
export const deleteFolder = (id) => api.delete(`/folders/${id}`).then((r) => r.data);
export const synthesizeFolder = (id) => api.post(`/folders/${id}/synthesize`, null, { timeout: 120000 }).then((r) => r.data);
export const folderRecommendedQuestions = (id) =>
    api.get(`/folders/${id}/recommended-questions`).then((r) => r.data);
export const folderNewSessionPrefill = (id) =>
    api.get(`/folders/${id}/new-session-prefill`).then((r) => r.data);
export const processChunk = (id, text) => api.post(`/sessions/${id}/chunks`, { text }).then((r) => r.data);

// ---- Meetings (Granola-style note taker) ----
// Meetings are sessions with kind="meeting". They share the session model
// and transcription pipeline but skip the interview suggestion engine.
export const listMeetings = () => api.get("/sessions", { params: { kind: "meeting" } }).then((r) => r.data);
export const createMeeting = (data) => api.post("/sessions", { ...data, kind: "meeting" }).then((r) => r.data);
export const addMeetingChunk = (id, text) => api.post(`/sessions/${id}/meeting-chunks`, { text }).then((r) => r.data);
export const saveMeetingNotes = (id, notes_raw) => api.patch(`/sessions/${id}/notes`, { notes_raw }).then((r) => r.data);
export const enhanceMeetingNotes = (id) => api.post(`/sessions/${id}/enhance-notes`, null, { timeout: 120000 }).then((r) => r.data);
export const askMeetings = (question, model) =>
    api.post("/meetings/chat", { question, ...(model ? { model } : {}) }, { timeout: 90000 }).then((r) => r.data);
export const askEverything = (question, model) =>
    api.post("/chat/all", { question, ...(model ? { model } : {}) }, { timeout: 90000 }).then((r) => r.data);
export const suggestionAction = (sid, qid, action, reason) =>
    api.post(`/sessions/${sid}/suggestions/${qid}/action`, { action, reason }).then((r) => r.data);
export const regenerateSuggestion = (sid, qid) =>
    api.post(`/sessions/${sid}/suggestions/${qid}/regenerate`, null, { timeout: 30000 }).then((r) => r.data);
export const addResearcherMessage = (sid, text, source = "manual") =>
    api.post(`/sessions/${sid}/researcher-messages`, { text, source }).then((r) => r.data);
export const completeSession = (id) => api.post(`/sessions/${id}/complete`, null, { timeout: 60000 }).then((r) => r.data);
export const resumeSession = (id) => api.post(`/sessions/${id}/resume`).then((r) => r.data);
export const regenerateSummary = (id) => api.post(`/sessions/${id}/summarize`).then((r) => r.data);
export const suggestGoalOrTitle = (payload) => api.post("/suggest/goal-title", payload).then((r) => r.data);
export const suggestSeedQuestions = (payload) => api.post("/suggest/seed-questions", payload).then((r) => r.data);
export const listTemplates = () => api.get("/templates").then((r) => r.data);
export const createTemplate = (data) => api.post("/templates", data).then((r) => r.data);
export const deleteTemplate = (id) => api.delete(`/templates/${id}`).then((r) => r.data);
// ---- Tasks (action items across meeting write-ups, as a to-do) ----
export const listTasks = () => api.get("/tasks").then((r) => r.data);
export const toggleTask = (sid, index, done) =>
    api.post(`/sessions/${sid}/tasks/${index}/toggle`, done === undefined ? {} : { done }).then((r) => r.data);

// ---- Second brain (knowledge graph) ----
// One call returns the whole graph: conversation nodes linked to the themes,
// people, and folders that connect them. Payload is counts + labels only.
export const getBrainGraph = () => api.get("/brain/graph").then((r) => r.data);

export const listParticipants = () => api.get("/participants").then((r) => r.data);
export const getParticipant = (name) => api.get(`/participants/${encodeURIComponent(name)}`).then((r) => r.data);
